// @ts-ignore
/* eslint-disable */
import { ParamsType } from '@ant-design/pro-components';
import { request } from '@umijs/max';
import { RestResponse } from 'mock/rest';

// Read about mocks here: https://v2-pro.ant.design/docs/mock-api

// --- Auth APIs ---
/** POST /auth/login */
export async function login(body: API.LoginParams, options?: { [key: string]: any }) {
  return request<API.LoginResult>('/api/auth/login', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    data: body,
    ...(options || {}),
  });
}

/** Obtain current user profile. Will be called after login to store in the initialState.
 *  GET /auth/me */
export async function currentUser(options?: { [key: string]: any }) {
  return request<API.CurrentUser>('/api/auth/me', {
    method: 'GET',
    ...(options || {}),
  });
}

/** POST /auth/logout */
export async function logout(options?: { [key: string]: any }) {
  // Temporarily store the accessToken for the logout API call below
  const accessToken = localStorage.getItem('accessToken');
  // Clear stored token from local storage
  localStorage.removeItem('accessToken');
  localStorage.removeItem('refreshToken');
  return request<Record<string, any>>('/api/auth/logout', {
    method: 'POST',
    headers: {
      Authorization: accessToken ? `Bearer ${accessToken}` : '',
    },
    ...(options || {}),
  });
}

// Helper function to handle token refresh
export async function refreshToken() {
  const refreshToken = localStorage.getItem('refreshToken');
  if (!refreshToken) {
    return null;
  }

  try {
    const response = await request<{
      accessToken: string;
      refreshToken: string;
    }>('/api/auth/refresh', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: refreshToken ? `Bearer ${refreshToken}` : '',
      },
    });

    if (response.accessToken) {
      localStorage.setItem('accessToken', response.accessToken);
      localStorage.setItem('refreshToken', response.refreshToken);
      console.log('Token renewed successfully');
      return response.accessToken;
    }
    return null;
  } catch (error) {
    console.error('Failed to refresh token:', error);
    return null;
  }
}

// Start calling refreshToken every 10 minutes.
// If there isn't valid token, refreshToken() does nothing.
window.setInterval(
  () => {
    console.log('Calling refresh token...');
    refreshToken();
  },
  10 * 60 * 1000,
);

// -- 2FA related --
/**
 * Generate QR code for 2FA setup
 * GET /auth/2fa/generate
 */
export async function generateQRcode2FA(options?: { [key: string]: any }): Promise<API.QrCode2FA> {
  const response = await request<RestResponse<API.QrCode2FA>>('/api/auth/2fa/generate', {
    method: 'GET',
    ...(options || {}),
  });
  if (response.status == 200 && response.data) {
    return response.data;
  } else {
    return {
      qrCodeUrl: '',
      secret: '',
    };
  }
}

/**
 * Verify 2FA
 * GET /auth/2fa/verify
 */
export async function verify2FA(
  data: { token: string },
  accessToken?: string,
  options?: { [key: string]: any },
): Promise<boolean> {
  const response = await request<RestResponse<boolean>>('/api/auth/2fa/verify', {
    method: 'POST',
    ...(accessToken && {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    }),
    ...(options || {}),
    data: data,
  });
  if (response.status == 200 && response.data) {
    return response.data;
  } else {
    return false;
  }
}

// -- Blocks ---

/** Return all blocks
 * GET /blocks */
export async function queryBlocks(options?: { [key: string]: any }): Promise<API.Block[]> {
  const response = await request<RestResponse<API.Block[]>>('/api/blocks', {
    method: 'GET',
    ...(options || {}),
  });
  if (response.status == 200) {
    return response.data || [];
  } else {
    return [];
  }
}

/** Get one block (all detail info) by id
 * GET /blocks/{id} */
export async function getBlock(
  id: string,
  options?: { [key: string]: any },
): Promise<API.Block | undefined> {
  const response = await request<RestResponse<API.Block>>(`/api/blocks/${id}`, {
    method: 'GET',
    ...(options || {}),
  });
  if (response.status == 200) {
    return response.data;
  } else {
    return undefined;
  }
}

/** Saves one given block, if does not exist it will create new
 * POST /blocks/{id} */
export async function saveBlock(
  block: API.Block,
  options?: { [key: string]: any },
): Promise<string> {
  const response = await request<RestResponse<string>>(`/api/blocks`, {
    method: 'POST',
    data: block,
    ...(options || {}),
  });

  if (response.status == 200) {
    return response.data || '';
  } else {
    return '';
  }
}

/** Delete given block by id
 * DELETE /blocks/{id} */
export async function deleteBlock(id: string, options?: { [key: string]: any }): Promise<boolean> {
  const response = await request<RestResponse<boolean>>(`/api/blocks/${id}`, {
    method: 'DELETE',
    ...(options || {}),
  });

  if (response.status == 200) {
    return true;
  } else {
    return false;
  }
}

/** Return general blocks
 * GET /blocks */
export async function queryGeneralBlocks(options?: { [key: string]: any }): Promise<API.Block[]> {
  const response = await request<RestResponse<API.Block[]>>('/api/general-blocks', {
    method: 'GET',
    ...(options || {}),
  });

  if (response.status == 200) {
    return response.data || [];
  } else {
    return [];
  }
}

/** Return section-specific blocks
 * GET /blocks */
export async function querySectionBlocks(options?: { [key: string]: any }): Promise<API.Block[]> {
  const response = await request<RestResponse<API.Block[]>>('/api/section-blocks', {
    method: 'GET',
    ...(options || {}),
  });

  if (response.status == 200) {
    return response.data || [];
  } else {
    return [];
  }
}

/** Return ads blocks
 * GET /blocks */
export async function queryAdsBlocks(options?: { [key: string]: any }): Promise<API.Block[]> {
  const response = await request<RestResponse<API.Block[]>>('/api/ads-blocks', {
    method: 'GET',
    ...(options || {}),
  });

  if (response.status == 200) {
    return response.data || [];
  } else {
    return [];
  }
}

/** Return list of post IDs, used in select post drop-down
 * GET /post-list/:section */
export async function listPosts(
  section: string,
  options?: { [key: string]: any },
): Promise<{ id: string; title: string }[]> {
  const response = await request<RestResponse<{ id: string; title: string }[]>>(
    `/api/post-list2/${section}`,
    {
      method: 'GET',
      ...(options || {}),
    },
  );

  if (response.status == 200) {
    return response.data || [];
  } else {
    return [];
  }
}

/** Return list of posts
 * GET /post/:section */
export async function queryPosts(
  section: string,
  params?: ParamsType & { pageSize?: number; current?: number },
  options?: { [key: string]: any },
): Promise<{ data: API.Post[]; metadata: { total: number } }> {
  const response = await request<RestResponse<API.Post[]>>(`/api/posts/${section}`, {
    method: 'GET',
    params: params,
    ...(options || {}),
  });

  if (response.status == 200) {
    return {
      data: response.data || [],
      metadata: {
        total: response.metadata?.total || 0,
      },
    };
  } else {
    return {
      data: [],
      metadata: {
        total: 0,
      },
    };
  }
}

/** Save one post
 * POST /post */
export async function savePost(
  section: string,
  data: API.Post,
  options?: { [key: string]: any },
): Promise<string> {
  const response = await request<RestResponse<string>>(`/api/posts/${section}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    data: data,
    ...(options || {}),
  });

  if (response.status == 200) {
    return response.data || '';
  } else {
    return '';
  }
}

/** Delete one post
 * DELETE /post/:section/:id */
export async function deletePost(id: string, options?: { [key: string]: any }): Promise<boolean> {
  const response = await request<RestResponse<boolean>>(`/api/posts/${id}`, {
    method: 'DELETE',
    ...(options || {}),
  });

  if (response.status == 200) {
    return true;
  } else {
    return false;
  }
}

/** Return site settings
 * GET /site */
export async function querySiteSetting(options?: { [key: string]: any }): Promise<API.SiteSetting> {
  const response = await request<RestResponse<API.SiteSetting>>(`/api/site`, {
    method: 'GET',
    ...(options || {}),
  });

  if (response.status == 200) {
    return response.data || ({} as API.SiteSetting);
  } else {
    return {} as API.SiteSetting;
  }
}

/** Save site settings
 * POST /site */
export async function saveSiteSetting(
  data: API.SiteSetting,
  options?: { [key: string]: any },
): Promise<string> {
  const response = await request<RestResponse<string>>(`/api/site`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    data: data,
    ...(options || {}),
  });

  if (response.status == 200) {
    return response.data || '';
  } else {
    return '';
  }
}

// --- User Management ---
/** Query list of all users
 * GET /users */
export async function queryUsers(options?: { [key: string]: any }): Promise<API.User[]> {
  const response = await request<RestResponse<API.User[]>>(`/api/users`, {
    method: 'GET',
    ...(options || {}),
  });

  if (response.status == 200) {
    return response.data || [];
  } else {
    return [];
  }
}

/** Create a new user
 * POST /users */
export async function createUser(
  data: API.UserDto,
  options?: { [key: string]: any },
): Promise<string> {
  const response = await request<RestResponse<string>>(`/api/users`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    data: data,
    ...(options || {}),
  });

  if (response.status == 200) {
    return response.data || '';
  } else {
    return '';
  }
}

/** Delete a new user
 * DELETE /users/:id */
export async function deleteUser(id: string, options?: { [key: string]: any }): Promise<boolean> {
  const response = await request<RestResponse<boolean>>(`/api/users/${id}`, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
    },
    ...(options || {}),
  });

  if (response.status == 200) {
    return true;
  } else {
    return false;
  }
}

/** Update password only for given user
 * POST /users/:id   */
export async function resetUserPassword(
  id: string,
  newPassword: string,
  options?: { [key: string]: any },
): Promise<boolean> {
  const response = await request<RestResponse<string>>(`/api/users/${id}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    data: {
      password: newPassword,
    },
    ...(options || {}),
  });

  if (response.status == 200) {
    return true;
  } else {
    return false;
  }
}

/** Update privileges only for given user
 * POST /users/:id/update-privileges */
export async function updateUserPrivileges(
  id: string,
  data: API.UserPrivilege[],
  options?: { [key: string]: any },
): Promise<boolean> {
  const response = await request<RestResponse<string>>(`/api/users/${id}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    data: {
      privileges: data,
    },
    ...(options || {}),
  });

  if (response.status == 200) {
    return true;
  } else {
    return false;
  }
}

/** Change own password
 * POST /change-password */
export async function changePassword(
  newPassword: string,
  options?: { [key: string]: any },
): Promise<boolean> {
  try {
    await request(`/api/auth/change-password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      data: {
        password: newPassword,
      },
      ...(options || {}),
    });

    // If we get here, the request was successful (no error thrown)
    return true;
  } catch (error) {
    console.error('Failed to change password:', error);
    return false;
  }
}
